import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, ChevronLeft, ChevronRight } from 'lucide-react';
type Tomo = {
  num: string;
  emoji: string;
  subtitle: string;
  title: string;
  image: string;
  paragraphs: string[];
  achievements: string[];
};
const tomos: Tomo[] = [{
  num: 'I',
  emoji: '📖',
  subtitle: 'Manual Principal',
  title: 'Orden Natal',
  image: 'https://cdn.magicpatterns.com/uploads/gDC6C2oaExASv8nZgFvFGb/Untitled_design_(5).png',
  paragraphs: ['La base de todo el sistema.', 'En este tomo aprenderás cómo funciona realmente una carta natal y descubrirás un método simple para dejar de sentirte perdida frente a tanta información.', 'Vas a comprender qué mirar primero, cómo organizar una interpretación y cómo desarrollar una comprensión sólida de los elementos más importantes de cualquier carta.'],
  achievements: ['Entender cómo se construye una carta natal', 'Aprender el lenguaje fundamental de la astrología', 'Identificar rápidamente los elementos más importantes', 'Crear una base sólida para interpretar cartas con confianza', 'Eliminar la sensación de confusión al comenzar']
}, {
  num: 'II',
  emoji: '✨',
  subtitle: 'Conexión Profunda',
  title: 'Conexión Natal',
  image: 'https://cdn.magicpatterns.com/uploads/rkARgB4YTvnGzF4exY3dKy/2.png',
  paragraphs: ['Aprendé a comprender las motivaciones, emociones y patrones internos que forman la esencia de una persona.', 'Este tomo te enseñará a ir más allá de los significados superficiales para descubrir la verdadera profundidad de una carta natal.'],
  achievements: ['Comprender la personalidad de manera más profunda', 'Interpretar energías internas y emocionales', 'Detectar patrones repetitivos', 'Entender fortalezas y desafíos personales', 'Crear lecturas mucho más significativas']
}, {
  num: 'III',
  emoji: '🌙',
  subtitle: 'Fluidez Natal',
  title: 'Fluidez Natal',
  image: 'https://cdn.magicpatterns.com/uploads/4ACJC7e7MKGpUytVSPDiiH/3.png',
  paragraphs: ['El sistema práctico para que una carta natal deje de parecer un rompecabezas.', 'Aprenderás cómo conectar signos, planetas y casas de manera natural para interpretar una carta completa sin quedarte bloqueada.'],
  achievements: ['Saber exactamente qué analizar primero', 'Conectar todos los elementos de una carta', 'Reducir errores de interpretación', 'Leer cartas con mayor rapidez', 'Ganar confianza en cada análisis']
}, {
  num: 'IV',
  emoji: '🔮',
  subtitle: 'Sesiones Natales',
  title: 'Sesiones Natales',
  image: 'https://cdn.magicpatterns.com/uploads/wAwWc1bY36aYjt52qCVNz9/4.png',
  paragraphs: ['Descubrí cómo transformar tu conocimiento en interpretaciones claras y fáciles de comunicar.', 'Este tomo está diseñado para ayudarte a estructurar lecturas completas y transmitir información de forma profesional.'],
  achievements: ['Organizar una lectura de principio a fin', 'Comunicar interpretaciones con claridad', 'Crear sesiones más profesionales', 'Guiar a otras personas con confianza', 'Dar estructura a cada consulta']
}, {
  num: 'V',
  emoji: '💞',
  subtitle: 'Sinastría y Compatibilidad',
  title: 'Sinastría & Compatibilidad',
  image: 'https://cdn.magicpatterns.com/uploads/8VNH4Dzqc1gwL3B2J2yxHk/5.png',
  paragraphs: ['Aprendé a analizar vínculos, relaciones y compatibilidades utilizando herramientas astrológicas de forma práctica.', 'Descubrirás cómo interpretar las dinámicas entre dos personas y comprender los aprendizajes que surgen dentro de cada relación.'],
  achievements: ['Analizar relaciones con mayor profundidad', 'Comprender compatibilidades y desafíos', 'Interpretar conexiones significativas', 'Detectar patrones relacionales', 'Realizar análisis comparativos completos']
}];
const slideVariants = {
  enter: () => ({
    opacity: 0
  }),
  center: {
    opacity: 1
  },
  exit: () => ({
    opacity: 0
  })
};
export function TomosSlider() {
  const [[index, direction], setIndex] = useState<[number, number]>([0, 0]);
  const current = tomos[index];
  const goTo = (next: number) => {
    const clamped = (next + tomos.length) % tomos.length;
    const dir = next > index ? 1 : -1;
    setIndex([clamped, dir]);
  };
  return <div className="relative" data-id="element-308" data-mp="3aiimj5vix5d0">
      {/* Tab strip — click any tomo to jump */}
      <div className="flex justify-center gap-2 md:gap-3 mb-10 flex-wrap px-2" data-id="element-309" data-mp="2wku21z5dc08h">
        {tomos.map((t, i) => {
        const active = i === index;
        return <button key={t.num} type="button" onClick={() => goTo(i)} className={'group relative flex items-center gap-2 px-4 md:px-5 py-2.5 md:py-3 rounded-xl border backdrop-blur-sm transition-all ' + (active ? 'bg-primary/15 border-primary/60 shadow-[0_0_25px_rgba(212,168,87,0.25)]' : 'bg-card/40 border-border/50 hover:border-primary/40 hover:bg-card/70')} aria-pressed={active} aria-label={`Ver COSMOS ${t.num} — ${t.title}`} data-id="element-310" data-mp="1l4wz370p3fs9" data-mpi={i}>
              <span className={'font-heading font-black text-sm md:text-base transition-colors ' + (active ? 'text-primary' : 'text-foreground/70')} data-id="element-311" data-mp="yy2o5oayppxe">
                {t.num}
              </span>
              <span className={'hidden sm:inline text-xs md:text-sm font-medium tracking-wide transition-colors ' + (active ? 'text-foreground' : 'text-muted-foreground')} data-id="element-312" data-mp="13vkzflcgch8k">
                {t.title}
              </span>
            </button>;
      })}
      </div>

      {/* Main slide area */}
      <div className="relative overflow-hidden rounded-3xl bg-card/60 border border-border/50 backdrop-blur-sm shadow-[0_0_40px_rgba(212,168,87,0.06)]" data-id="element-313" data-mp="2g8hs1ltwp5e4">
        {/* Prev / Next arrows */}
        <button type="button" onClick={() => goTo(index - 1)} aria-label="Tomo anterior" className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 z-20 flex items-center justify-center w-10 h-10 md:w-12 md:h-12 rounded-full bg-background/70 border border-border/60 backdrop-blur-md text-foreground/80 hover:text-primary hover:border-primary/50 transition-all" data-id="element-314" data-mp="2p24kp32thsj3">
          <ChevronLeft className="w-5 h-5 md:w-6 md:h-6" data-id="element-315" data-mp="26bdda72e4c7c" />
        </button>
        <button type="button" onClick={() => goTo(index + 1)} aria-label="Próximo tomo" className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 z-20 flex items-center justify-center w-10 h-10 md:w-12 md:h-12 rounded-full bg-background/70 border border-border/60 backdrop-blur-md text-foreground/80 hover:text-primary hover:border-primary/50 transition-all" data-id="element-316" data-mp="3crfoyujdm51z">
          <ChevronRight className="w-5 h-5 md:w-6 md:h-6" data-id="element-317" data-mp="3i3w0lj9r673e" />
        </button>

        <div className="relative px-6 sm:px-10 md:px-16 py-12 md:py-14 min-h-[640px] md:min-h-[560px]" data-id="element-318" data-mp="3e2si0p0blwyh">
          <AnimatePresence mode="wait" custom={direction} initial={false} data-id="element-319" data-mp="393fxwc0u33zl">
            <motion.div key={current.num} custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
            duration: 0.45,
            ease: [0.16, 1, 0.3, 1]
          }} className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)] gap-8 md:gap-12 items-center" data-id="element-320" data-mp="391cwkvu8dofk">
              {/* Book image */}
              <div className="relative flex items-center justify-center h-[19.8rem] md:h-[462px] bg-transparent" data-id="element-321" data-mp="tah1gszeqpvh">
                <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[78%] h-[78%] bg-primary/[0.08] rounded-full blur-3xl pointer-events-none" data-id="element-322" data-mp="1gmwxly3p34ou"></div>
                <motion.img src={current.image} alt={`COSMOS ${current.num} — ${current.title}`} className="relative z-10 mx-auto h-full w-auto max-w-full object-contain bg-transparent drop-shadow-[0_25px_50px_rgba(212,168,87,0.28)]" initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} transition={{
                duration: 0.4,
                ease: 'easeOut'
              }} data-id="element-323" data-mp="12tfgejyhe41z" />
              </div>

              {/* Content */}
              <div data-id="element-324" data-mp="2ebq7f0zf30g9">
                <div className="flex items-center gap-3 mb-4" data-id="element-325" data-mp="2ebgi7fm74xbm">
                  <span className="text-3xl md:text-4xl" aria-hidden="true" data-id="element-326" data-mp="2ja1krlox7t38">
                    {current.emoji}
                  </span>
                  <div className="flex items-center justify-center w-11 h-11 rounded-lg bg-primary/10 border border-primary/30 font-heading font-black text-lg text-primary" data-id="element-327" data-mp="2tdyeem5pvzn7">
                    {current.num}
                  </div>
                </div>

                <p className="text-xs uppercase tracking-[0.3em] text-primary/80 font-medium mb-2" data-id="element-328" data-mp="2xf1v1avl9zph">
                  COSMOS {current.num}
                </p>
                <h3 className="font-heading font-bold text-2xl md:text-3xl lg:text-4xl leading-tight text-foreground mb-5" data-id="element-329" data-mp="3c610id14o84u">
                  {current.subtitle}
                </h3>

                <div className="space-y-3 mb-7" data-id="element-330" data-mp="3l436z83mrszi">
                  {current.paragraphs.map((p, i) => <p key={i} className="text-sm md:text-base text-foreground/85 leading-relaxed" data-id="element-331" data-mp="3h16kv7evteqt" data-mpi={i}>
                      {p}
                    </p>)}
                </div>

                <p className="text-sm md:text-base font-heading font-semibold text-primary mb-4 uppercase tracking-wider" data-id="element-332" data-mp="24v3d221h709x">
                  Lo que vas a lograr
                </p>
                <ul className="space-y-2.5" data-id="element-333" data-mp="1vtn3txmed47a">
                  {current.achievements.map((a, i) => <motion.li key={i} initial={{
                  opacity: 0,
                  x: -10
                }} animate={{
                  opacity: 1,
                  x: 0
                }} transition={{
                  duration: 0.4,
                  delay: 0.1 + i * 0.06,
                  ease: 'easeOut'
                }} className="flex items-start gap-3" data-id="element-334" data-mp="1qvljl9d8iyu8" data-mpi={i}>
                      <span className="shrink-0 mt-0.5 flex items-center justify-center w-5 h-5 rounded-full bg-primary/10 border border-primary/30" data-id="element-335" data-mp="32ilnriep6cgk">
                        <Check className="w-3 h-3 text-primary" strokeWidth={3} data-id="element-336" data-mp="39v1l5g3mxl45" />
                      </span>
                      <span className="text-sm md:text-base text-foreground/90 leading-snug" data-id="element-337" data-mp="3jqwa0sygccfd">
                        {a}
                      </span>
                    </motion.li>)}
                </ul>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Dot indicators */}
        <div className="flex items-center justify-center gap-2 pb-7" data-id="element-338" data-mp="1k87nause3de">
          {tomos.map((_, i) => <button key={i} type="button" onClick={() => goTo(i)} aria-label={`Ir a tomo ${i + 1}`} className={'h-1.5 rounded-full transition-all ' + (i === index ? 'w-8 bg-primary shadow-[0_0_10px_rgba(212,168,87,0.5)]' : 'w-1.5 bg-border hover:bg-primary/50')} data-id="element-339" data-mp="6fdr830pv5yb" data-mpi={i} />)}
        </div>
      </div>
    </div>;
}